import React, {useEffect, useState} from "react";
import {
  Box,
  Button,
  VStack,
  Text,
  HStack,
  Center,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Icon,
} from "@chakra-ui/react";
import { AddIcon, ArrowBackIcon, ChevronDownIcon, DeleteIcon, SearchIcon } from "@chakra-ui/icons";
import { Header } from "build-ts/metadata";
import { ColumnType, columnTypeToJSON } from "build-ts/enums";
import EditKGColumnComponent from "./mkg/EditKGColumnComponent";
import { useConfigurationContext } from "context/ConfigurationContext";
import CreateKGColumnComponent from "components/configuration/mkg/CreateKGColumnComponent";
import CustomModal from "../modals/CustomModal";
import DependenciesModal from "./DependenciesModal";
import { FaChartPie, FaCheckCircle, FaFilter } from "react-icons/fa";
import { MdEdit, MdNumbers } from "react-icons/md";
import { FiCalendar } from "react-icons/fi";

interface KGColumnListComponentProps {
  onBack: () => void;
  kgColumnList: Header[];
  setKGColumnList: React.Dispatch<React.SetStateAction<Header[]>>;
  columnType: ColumnType;
  refreshKgColumns: () => void;
  onReloadKnowledgeGraph?: () => void;
}

const KGColumnListComponent: React.FC<KGColumnListComponentProps> = ({ 
  onBack,
  kgColumnList,
  setKGColumnList,
  columnType,
  refreshKgColumns,
  onReloadKnowledgeGraph
 }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isCreateKGColumnOpen, setIsCreateKGColumnOpen] = useState<boolean>(false);
  const {
    selectedKgColumn, 
    setSelectedKgColumn,
    setSelectedMetric,
    setSelectedBenchmark,
    handleEditColumnModal, 
    editColumn,  
    kgColumnModalState,
    deleteKgColumnDialog,
    handleDeleteKgColumnDialog,
    handleDeleteKgColumn,
    closeDeleteKgColumnDialog,
    closeDeleteKgColumnModal,
    closeKgColumnModal,
    dependencies,
    showDependenciesModal,
  } = useConfigurationContext();

  const cancelDeleteRef = React.useRef<HTMLButtonElement>(null);
  const [triggerSubmit, setTriggerSubmit] = useState<boolean>(false);

  // Fetch KG columns on component mount
  useEffect(() => {
    refreshKgColumns();
  }, []);

  const handleKGColumnClick = (kgColumn: Header) => {
    if(selectedKgColumn?.id === kgColumn.id) {
      setSelectedKgColumn(null);
    } else {
      setSelectedKgColumn(kgColumn);
    }
    setSelectedMetric(null);
    setSelectedBenchmark(null);
  };

  const handleCloseModal = () => {
    closeKgColumnModal();
  };

  const filteredKGColumns = kgColumnList.filter(kgColumn =>{
    return kgColumn?.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleOpenCreateKGColumn = () => {
    setIsCreateKGColumnOpen(true);
    kgColumnModalState.onOpen();
  };

  const handleCloseCreateKGColumn = () => {
    setIsCreateKGColumnOpen(false);
    kgColumnModalState.onClose();
  };

  // Function to trigger submit
  const handleSave = () => {
    setTriggerSubmit(true);
  };

  // Save button
  const saveButton = (
    <Button
      colorScheme="green"
      size="sm"
      onClick={handleSave}
      leftIcon={<FaCheckCircle />}
      fontWeight="bold"
    >
      Save
    </Button>
  );

  return (
    <Box height="100vh" display="flex" flexDirection="column">
      {/* Back Button, Search, and Header in same line */}
      <Box px={3} py={3} flexShrink={0}>
        <HStack spacing={3} alignItems="center" width="100%">
          <IconButton
            aria-label="Back"
            icon={<ArrowBackIcon boxSize={5} />}
            variant="tertiary"
            size="xs"
            onClick={onBack}
            _hover={{ bg: "purple.50", color: "black" }}
            flexShrink={0}
            fontSize="sm"
          />
          
          <InputGroup flex="1" maxW="400px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="sm"
              borderRadius="md"
              bg="white"
              _focus={{
                borderColor: "purple.700",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
              }}
            />
          </InputGroup>
          
          <Button
            onClick={() => handleOpenCreateKGColumn()}
            leftIcon={<AddIcon boxSize={3}/>}
            size="xs"
            aria-label="Create New Plan"
            borderRadius="md"
            title="Create New Plan"
            bg="gray.50"
            color="purple.700"
            _hover={{ bg: "purple.700", color: "white" }}
            variant="outline"
            borderWidth="2px"
            borderColor="purple.700"
            fontWeight="700"
            iconSpacing={1}
            cursor="pointer"
          >
            New
          </Button>
        </HStack>
      </Box>

      <Box 
        flex="1" 
        overflowY="auto"
        overflowX="hidden"
        px={4}
        pb={14}
        minHeight={0}
        sx={{
          "::-webkit-scrollbar": {
            width: "6px",
          },
          "::-webkit-scrollbar-track": {
            background: "gray.100",
            borderRadius: "3px",
          },
          "::-webkit-scrollbar-thumb": {
            background: "purple.300",
            borderRadius: "3px",
          },
          "::-webkit-scrollbar-thumb:hover": {
            background: "purple.400",
          },
        }}
      >
        <VStack spacing={2} align="stretch">
        <HStack justify="space-between" align="center" mx={1} p={0}>
            {columnType === ColumnType.MEASURE ? <Icon as={MdNumbers} boxSize={5} color="gray.500"/> 
            : columnType === ColumnType.ATTRIBUTE ? <Icon as={FaChartPie} boxSize={5} color="gray.500"/>
            : <Icon as={FiCalendar} boxSize={5} color="gray.500"/>}
            <Text 
              fontSize="xs" 
              fontWeight="bold" 
              color="gray.500" 
              letterSpacing="wider"
              textTransform="uppercase"
              mb={0}
            >
              {columnTypeToJSON(columnType).toString()+"S"}
            </Text>
            {/* Divider Line */}
            <Box 
              flex="1" 
              height="1px" 
              bg="gray.300" 
              mx={0}
            />
            <IconButton
              aria-label="Filter metrics"
              icon={<Icon as={FaFilter} />}
              size="xs"
              variant="ghost"
              color="gray.500"
              _hover={{ color: "purple.600", bg: "gray.50" }}
              cursor="pointer"
            />
          </HStack>
          {filteredKGColumns.length>0 ? (
            filteredKGColumns.map((kgColumn) => (
              <Button
                key={kgColumn.id}
                variant="outline"
                size="sm"
                justifyContent="flex-start"
                borderColor="gray.300"
                _hover={{ 
                  bg: selectedKgColumn?.id === kgColumn.id ? "purple.700" : "purple.50", 
                  color: selectedKgColumn?.id === kgColumn.id ? "white" : "gray.800"
                }}
                bg={selectedKgColumn?.id === kgColumn.id ? "purple.700" : "white"}
                color={selectedKgColumn?.id === kgColumn.id ? "white" : "gray.800"}
                borderRadius="md"
                fontSize="sm"
                height="auto"
                py={1}
                whiteSpace="normal"
                textAlign="left"
                onClick={() => handleKGColumnClick(kgColumn)}
              >
                {/* {kgColumn.name} */}
                <HStack justifyContent="space-between" width="100%">
                  <Text mb={0}>{kgColumn.name}</Text>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Options"
                      borderRadius="full"
                      icon={<ChevronDownIcon />}
                      variant="ghost"
                      size="sm"
                      onClick={(event) => {
                          event.stopPropagation();
                      }}
                    />
                    <MenuList>
                      <MenuItem
                        fontSize="sm"
                        fontWeight={600}
                        icon={<MdEdit color="gray.700"/>}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedKgColumn(null as any);
                          handleEditColumnModal(kgColumn);
                        }}
                        cursor="pointer"
                        color="gray.800"
                        _hover={{ bg: "gray.50" }}
                        py={2}
                        px={3}
                      >
                        Definition
                      </MenuItem>
                      <MenuItem
                        fontSize="sm"
                        fontWeight={600}
                        icon={<DeleteIcon color="red.500"/>}
                        onClick={() => {
                          setSelectedKgColumn(null as any);
                          handleDeleteKgColumnDialog(kgColumn?.id!);
                        }}
                        cursor="pointer"
                        color="red.500"
                        _hover={{ bg: "gray.50" }}
                        py={2}
                        px={3}
                      >
                        Delete
                      </MenuItem>
                      </MenuList>
                  </Menu>
                </HStack>
              </Button>
            ))
          ) : (
            <Center py={8}>
              <Text color="gray.500">No KG columns found</Text>
            </Center>
          )}
        </VStack>
      </Box>

      {/* Edit Modal */}
      {editColumn && (
        <CustomModal
          isOpen={kgColumnModalState.isOpen}
          onClose={handleCloseModal}
          title="Edit KG Column Configuration"
          size="xl"
          maxWidth="800px"
          headerAction={saveButton}
        >
          <EditKGColumnComponent
            id={selectedKgColumn?.id}
            onClose={handleCloseModal}
            onSave={refreshKgColumns}

            // For saving the KG Column
            triggerSubmit={triggerSubmit}
            setTriggerSubmit={setTriggerSubmit}
            onReloadKnowledgeGraph={onReloadKnowledgeGraph}
          />
        </CustomModal>
      )}

        {/* Create KG Column Modal */}
        {isCreateKGColumnOpen && (
          <CustomModal
            isOpen={kgColumnModalState.isOpen}
            onClose={handleCloseCreateKGColumn}
            title="Create New KG Column"
            size="xl"
            maxWidth="800px"
          >
            <CreateKGColumnComponent 
              onClose={handleCloseCreateKGColumn}
              colType={columnType}
              refreshKGColumns={refreshKgColumns}
            />
          </CustomModal>
        )}

      {deleteKgColumnDialog && (
        <AlertDialog
          isOpen={deleteKgColumnDialog}
          leastDestructiveRef={cancelDeleteRef}
          onClose={closeDeleteKgColumnDialog}
        >
          <AlertDialogOverlay
            style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
          >
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete KG Column
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to delete this {columnTypeToJSON(columnType).toString().charAt(0).toUpperCase() + columnTypeToJSON(columnType).toString().slice(1).toLowerCase()}?
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button
                  ref={cancelDeleteRef}
                  colorScheme="green"
                  onClick={closeDeleteKgColumnDialog}
                  >
                  Cancel
                  </Button>
                  <Button
                  colorScheme="red"
                  onClick={() => handleDeleteKgColumn(selectedKgColumn?.id!)}
                  ml={3}
                  >
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
      {/* KG Column Dependencies Modal */}
      {dependencies.length > 0 && (
        <DependenciesModal
          isOpen={showDependenciesModal}
          onClose={closeDeleteKgColumnModal}
          dependencies={dependencies}
        />
      )}
    </Box>
  );
};

export default KGColumnListComponent;